import type {
  CustomerSortBy,
  CustomerStatusFilter,
  LastLoginBandFilter,
  SortDir,
} from '../../api/customer';

/**
 * State of the Customer Information list (search, Access filter, Last login
 * filter, Account manager filter, sort and pagination), kept in the URL query so reload and
 * back/forward keep it.
 *
 * Pure on purpose: no React and no router import, so every rule is
 * unit-tested; the page only wires these helpers to `useSearchParams`.
 */
export type CustomerListQuery = {
  /** Free-text search, as typed. */
  q: string;
  status: CustomerStatusFilter | '';
  lastLoginBand: LastLoginBandFilter | '';
  /**
   * '' for all, `ACCOUNT_MANAGER_FILTER_NONE` for "Not assigned", else a
   * manager key. Keys are opaque (a HubSpot owner id or `ds_<id>`), so any
   * non-empty value is kept; DS validates it.
   */
  accountManager: string;
  sortBy: CustomerSortBy;
  sortDir: SortDir;
  /** 1-based. */
  page: number;
  perPage: number;
};

export const CUSTOMER_STATUS_FILTERS: readonly CustomerStatusFilter[] = [
  'active',
  'inactive',
  'pending',
];

export const LAST_LOGIN_BAND_FILTERS: readonly LastLoginBandFilter[] = [
  'recent',
  'stale',
  'old',
  'never',
];

export const CUSTOMER_SORT_COLUMNS: readonly CustomerSortBy[] = ['lastLoginAt', 'createdAt'];

export const SORT_DIRECTIONS: readonly SortDir[] = ['asc', 'desc'];

/** Page sizes the list offers: the default plus the pagination selector's options. */
export const CUSTOMER_LIST_PER_PAGE_OPTIONS: readonly number[] = [10, 15, 25, 50];

export const DEFAULT_CUSTOMER_LIST_QUERY: CustomerListQuery = Object.freeze({
  q: '',
  status: '',
  lastLoginBand: '',
  accountManager: '',
  sortBy: 'createdAt',
  sortDir: 'desc',
  page: 1,
  perPage: 15,
});

/** URL keys, one per `CustomerListQuery` field. */
export const CUSTOMER_LIST_QUERY_KEYS = {
  q: 'q',
  status: 'status',
  lastLoginBand: 'lastLoginBand',
  accountManager: 'accountManager',
  sortBy: 'sortBy',
  sortDir: 'sortDir',
  page: 'page',
  perPage: 'perPage',
} as const satisfies Record<keyof CustomerListQuery, string>;

const oneOf = <T extends string>(
  allowed: readonly T[],
  raw: string | null,
  fallback: T | '',
): T | '' => {
  const value = (raw ?? '').trim().toLowerCase();
  return (allowed as readonly string[]).includes(value) ? (value as T) : fallback;
};

const exactOneOf = <T extends string>(allowed: readonly T[], raw: string | null, fallback: T): T =>
  raw !== null && (allowed as readonly string[]).includes(raw) ? (raw as T) : fallback;

const positiveInt = (raw: string | null, fallback: number): number => {
  if (raw === null || !/^\d+$/.test(raw.trim())) return fallback;
  const value = Number(raw.trim());
  return Number.isSafeInteger(value) && value >= 1 ? value : fallback;
};

/**
 * Reads the list state from the URL. Unknown or invalid values fall back to
 * the defaults; `status` and `sortDir` are matched case-insensitively, so an
 * old bookmark with `status=ACTIVE` still reads as `active`.
 */
export function readCustomerListQuery(params: URLSearchParams): CustomerListQuery {
  const K = CUSTOMER_LIST_QUERY_KEYS;
  const D = DEFAULT_CUSTOMER_LIST_QUERY;
  const perPage = positiveInt(params.get(K.perPage), D.perPage);
  return {
    q: params.get(K.q) ?? D.q,
    status: oneOf(CUSTOMER_STATUS_FILTERS, params.get(K.status), D.status),
    lastLoginBand: oneOf(LAST_LOGIN_BAND_FILTERS, params.get(K.lastLoginBand), D.lastLoginBand),
    accountManager: (params.get(K.accountManager) ?? D.accountManager).trim(),
    sortBy: exactOneOf(CUSTOMER_SORT_COLUMNS, params.get(K.sortBy), D.sortBy),
    sortDir: (oneOf(SORT_DIRECTIONS, params.get(K.sortDir), D.sortDir) || D.sortDir) as SortDir,
    page: positiveInt(params.get(K.page), D.page),
    perPage: CUSTOMER_LIST_PER_PAGE_OPTIONS.includes(perPage) ? perPage : D.perPage,
  };
}

/** Writes the list state to URL params, omitting every value equal to its default. */
export function writeCustomerListQuery(query: CustomerListQuery): URLSearchParams {
  const params = new URLSearchParams();
  const K = CUSTOMER_LIST_QUERY_KEYS;
  const D = DEFAULT_CUSTOMER_LIST_QUERY;
  (Object.keys(K) as (keyof CustomerListQuery)[]).forEach((field) => {
    const value = query[field];
    if (value === D[field] || value === '') return;
    params.set(K[field], String(value));
  });
  return params;
}

/**
 * Applies a change to the list state. Any change of a field other than `page`
 * sends the list back to page 1 (a filtered or re-sorted list starts at the
 * top); a patch that only moves the page keeps everything else.
 */
export function withChange(
  query: CustomerListQuery,
  patch: Partial<CustomerListQuery>,
): CustomerListQuery {
  const next = { ...query, ...patch };
  const changesMoreThanPage = (Object.keys(patch) as (keyof CustomerListQuery)[]).some(
    (field) => field !== 'page' && patch[field] !== query[field],
  );
  if (changesMoreThanPage) return { ...next, page: 1 };
  return { ...next, page: positiveInt(String(next.page), 1) };
}

/**
 * Header click on a sortable column: a new column starts descending (most
 * recent first), the same column flips its direction; both go to page 1.
 */
export function toggleSort(query: CustomerListQuery, column: CustomerSortBy): CustomerListQuery {
  const sortDir: SortDir =
    query.sortBy === column ? (query.sortDir === 'desc' ? 'asc' : 'desc') : 'desc';
  return { ...query, sortBy: column, sortDir, page: 1 };
}
